import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LessonModel } from '../../../../Models/LessonModel';
import type { StudentContactModel, StudentModel } from '../../../../Models/StudentModel';
import type { PaymentProfileModel } from '../../../../Models/UserModel';
import { PremiumFeature } from '../../../../Models/enums';
import { useAppSelector } from '../../../../Redux/hooks';
import { canUse } from '../../../../Redux/sessionSlice';
import { DocumentService } from '../../../../Services/PublicService';
import { UserService } from '../../../../Services/UserService';
import { formatDate } from '../../../../Utils/DateUtil';
import { lessonStatusOf } from '../../../../Utils/LessonStatusUtil';
import { amountValue } from '../../../../Utils/MoneyUtil';
import { openWhatsAppAfter, whatsAppUrl } from '../../../../Utils/WhatsAppUtil';
import { routes } from '../../../../Utils/routes';
import { partyDisplayName } from '../../../../Utils/DisplayNameUtil';
import { useTranslation } from '../../../../i18n/useTranslation';
import type { TranslationKey } from '../../../../i18n/he';
import type { Language } from '../../../../Models/enums';
import { Modal } from '../../../SharedArea/Modal/Modal';
import './DebtReminderAction.css';

interface DebtReminderActionProps {
  student: StudentModel;
  lessons: readonly LessonModel[];
}

interface Recipient {
  id: string;
  name: string;
  phone: string;
}

/** The same debt-chasing action used by the legacy student card. */
export function DebtReminderAction({ student, lessons }: DebtReminderActionProps) {
  const { tr, language } = useTranslation();
  const navigate = useNavigate();
  const unlocked = useAppSelector(state => canUse(state.session, PremiumFeature.PaymentReminders));
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const recipients = paymentRecipients(student);

  function act() {
    if (!unlocked) {
      navigate(routes.premiumArea);
      return;
    }
    if (recipients.length > 1) {
      setChoosing(true);
      return;
    }
    if (recipients.length === 1) send(recipients[0]);
    else void copyReminder();
  }

  function send(recipient: Recipient) {
    setChoosing(false);
    setBusy(true);
    void openWhatsAppAfter(async () => {
      const [documentResult, profileResult] = await Promise.allSettled([
        DocumentService.createPaymentRequest(
          student.name,
          lessons.map(lesson => lesson.id),
          recipient.id === 'student' ? undefined : recipient.name
        ),
        UserService.getPaymentProfile()
      ]);
      const document = documentResult.status === 'fulfilled' ? documentResult.value : null;
      const profile = profileResult.status === 'fulfilled' ? profileResult.value : null;
      const documentUrl = document ? `${window.location.origin}/pay/${document.token}` : null;
      return whatsAppUrl(recipient.phone, buildMessage(student, lessons, recipient, profile, documentUrl, tr, language));
    }).finally(() => setBusy(false));
  }

  async function copyReminder() {
    setBusy(true);
    try {
      const profile = await UserService.getPaymentProfile().catch(() => null);
      const payer = student.contacts.find(contact => contact.receivesPayments === 1) ?? null;
      const message = buildMessage(student, lessons, payer, profile, null, tr, language);
      await navigator.clipboard.writeText(message);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission is controlled by the browser. A prompt keeps the
      // prepared message available instead of silently discarding it.
      const message = buildMessage(student, lessons, null, null, null, tr, language);
      window.prompt(tr('copyReminderPrompt'), message);
    } finally {
      setBusy(false);
    }
  }

  const sendable = unlocked && recipients.length > 0;
  const label = copied
    ? tr('reminderCopied')
    : !unlocked || sendable
      ? tr('sendReminderShort')
      : tr('copyReminderShort');
  const title = !unlocked ? tr('reminderLockedTitle') : sendable ? tr('sendReminderWhatsApp') : tr('copyReminderNoPhone');

  return (
    <>
      <button
        type="button"
        className={`student-reminder-button${sendable ? ' is-sendable' : ''}${unlocked ? '' : ' is-locked'}`}
        onClick={act}
        disabled={busy}
        title={title}
        aria-label={title}
      >
        {!unlocked ? <LockIcon /> : sendable ? <WhatsAppIcon /> : <CopyIcon />}
        <span>{label}</span>
      </button>

      {choosing && (
        <Modal title={tr('chooseReminderRecipient')} subtitle={tr('chooseReminderRecipientHint')} onClose={() => setChoosing(false)}>
          <div className="reminder-recipient-list">
            {recipients.map(recipient => (
              <button key={recipient.id} type="button" className="reminder-recipient" onClick={() => send(recipient)}>
                <WhatsAppIcon />
                <span><strong>{partyDisplayName(recipient.name, [student], language)}</strong><bdi dir="ltr">{recipient.phone}</bdi></span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function paymentRecipients(student: StudentModel): Recipient[] {
  const contacts = student.contacts
    .filter(contact => contact.receivesPayments === 1 && Boolean(whatsAppUrl(contact.phone, '')))
    .map(contact => ({ id: `contact-${contact.id}`, name: contact.name, phone: contact.phone! }));
  if (contacts.length > 0) return contacts;
  return whatsAppUrl(student.phone, '')
    ? [{ id: 'student', name: student.name, phone: student.phone! }]
    : [];
}

type Translate = (key: TranslationKey, ...values: Array<string | number>) => string;

function buildMessage(
  student: StudentModel,
  lessons: readonly LessonModel[],
  recipient: Pick<Recipient, 'id' | 'name'> | Pick<StudentContactModel, 'name'> | null,
  profile: PaymentProfileModel | null,
  documentUrl: string | null,
  tr: Translate,
  language: Language
): string {
  const ordered = [...lessons].sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? ''));
  const total = ordered.reduce((sum, lesson) => sum + amountValue(lesson.amount), 0);
  const studentFirstName = student.firstName || firstName(student.name);
  const addressedToStudent = !recipient || ('id' in recipient && recipient.id === 'student');
  const greetingName = addressedToStudent ? studentFirstName : firstName(recipient.name);
  const lines = [
    tr('reminderHello', greetingName || student.name),
    '',
    addressedToStudent ? tr('reminderIntro') : tr('reminderIntroFor', studentFirstName || student.name),
    '',
    ...ordered.map(lesson => tr(
      'reminderLessonLine',
      formatDate(lesson.date, language),
      lesson.time || '',
      amountValue(lesson.amount),
      lessonStatusOf(lesson) === 'cancelled_charged' ? tr('reminderCancelledNote') : ''
    )),
    '',
    tr('reminderTotal', total)
  ];
  if (documentUrl) lines.push('', tr('reminderDocumentIntro'), documentUrl);
  lines.push(...paymentLines(profile, tr, language), '', tr('reminderThanks'), tr('reminderSignature'), 'magbit.shop');
  return lines.join('\r\n');
}

function paymentLines(profile: PaymentProfileModel | null, tr: Translate, language: Language): string[] {
  if (!profile) return [];
  // The message is composed in the tutor's language, so its bank details are too.
  const bankName = language === 'en' ? profile.bankNameEn?.trim() || profile.bankName : profile.bankName;
  const bankOwner = language === 'en' ? profile.bankOwnerEn?.trim() || profile.bankOwner : profile.bankOwner;
  const details: string[] = [];
  if (profile.payboxLink) details.push(`${tr('paybox')}:`, profile.payboxLink, '');
  if (profile.bitLink) details.push(`${tr('bit')}:`, profile.bitLink, '');
  const bank = [
    bankName && `${tr('bankName')} ${bankName}`,
    profile.bankBranch && `${tr('bankBranch')} ${profile.bankBranch}`,
    profile.bankAccount && `${tr('bankAccount')} ${profile.bankAccount}`
  ].filter(Boolean).join(', ');
  if (bank || bankOwner) {
    details.push(`${tr('bankTransfer')}:`);
    if (bank) details.push(bank);
    if (bankOwner) details.push(`${tr('accountOwner')} ${bankOwner}`);
  }
  while (details.at(-1) === '') details.pop();
  return details.length ? ['', tr('reminderHowToPay'), '', ...details] : [];
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

function WhatsAppIcon() {
  return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.15h-.01A8.2 8.2 0 0 1 7.84 19l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 4.55 0 8.24 3.7 8.24 8.24 0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-2.16-1.08-3.58-1.93-5.02-4.38-.14-.25-.01-.38.12-.5.31-.3.62-.66.74-1.08.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66 2.45 1.06 2.45.71 2.89.67.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.17-.47-.29z"/></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>;
}

function CopyIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7" y="7" width="13" height="13" rx="2"/><path d="M4 16V6a2 2 0 0 1 2-2h10"/></svg>;
}
